import React, { useMemo } from 'react';
import Draggable from 'react-draggable';

import SVG from 'components/widgets/icons/svg';
import useMfaConnection from 'routes/mfa/hooks/use-mfa-connection';

import useMfaPanelStore from './store';

const CARD_WIDTH = 360;
const CARD_CHROME = 88;
const FALLBACK_DEVICE_HEIGHT = Math.round(CARD_WIDTH * 20 / 9);
const TRIAGE_RESERVE = 316;
const CASCADE = 28;

const cardStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: CARD_WIDTH,
  background: '#ffffff',
  border: '1px solid #d5dae0',
  borderRadius: 10,
  boxShadow: '0 12px 32px rgba(20, 30, 45, 0.28)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const titlebarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 12px',
  background: '#2c3e50',
  color: '#fff',
  cursor: 'move',
  userSelect: 'none'
};

const controlsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderBottom: '1px solid #eef0f3'
};

export default function MfaPanelCard({ deviceId, z, index, hasTriagePane }) {
  const closePanel = useMfaPanelStore((s) => s.closePanel);
  const focusPanel = useMfaPanelStore((s) => s.focusPanel);
  const isActive = useMfaPanelStore((s) => s.activeDeviceId === deviceId);

  const {
    device,
    status,
    resolution,
    setContainerRef,
    connect,
    disconnect
  } = useMfaConnection({ deviceId, isActive });

  const deviceHeight = resolution
    ? Math.round(CARD_WIDTH * (resolution.height / resolution.width))
    : FALLBACK_DEVICE_HEIGHT;
  const cardHeight = deviceHeight + CARD_CHROME;

  // Computed once on mount: cascade down/left from the bottom-right, clearing
  // the triage pane. Draggable is uncontrolled, so the card keeps its own
  // position after this initial placement.
  const { defaultPosition, bounds } = useMemo(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const offset = index * CASCADE;
    const reserve = hasTriagePane ? TRIAGE_RESERVE : 0;
    return {
      defaultPosition: {
        x: Math.max(8, vw - CARD_WIDTH - reserve - offset),
        y: Math.max(8, vh - cardHeight - 24 - offset)
      },
      bounds: {
        left: 8,
        top: 8,
        right: Math.max(8, vw - CARD_WIDTH - 8),
        bottom: Math.max(8, vh - 80) // keep at least the title bar on-screen
      }
    };
  }, [hasTriagePane]);

  const title = device ? device.name : 'MFA Connect';

  let statusColor = '#f44336';
  if (status === 'connected') statusColor = '#4caf50';
  else if (status === 'connecting') statusColor = '#ff9800';

  return (
    <Draggable
      handle=".mfa-card__titlebar"
      defaultPosition={defaultPosition}
      bounds={bounds}
      onStart={() => focusPanel(deviceId)}
    >
      <div
        style={{ ...cardStyle, zIndex: z }}
        onMouseDown={() => focusPanel(deviceId)}
      >
        <div className="mfa-card__titlebar" style={titlebarStyle}>
          {device?.team?.color && (
            <span
              className="rounded-circle"
              style={{ width: 11, height: 11, backgroundColor: device.team.color, flex: '0 0 11px' }}
            />
          )}
          <span style={{ flex: 1, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </span>
          <button
            type="button"
            onClick={() => closePanel(deviceId)}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex', opacity: 0.85 }}
          >
            <SVG src="svg/close.svg" />
          </button>
        </div>

        <div style={controlsStyle}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: statusColor }} />
          <span style={{ textTransform: 'capitalize', fontSize: 13, flex: 1 }}>{status}</span>
          {status !== 'connected' && status !== 'connecting' && (
            <button className="btn btn-sm btn-primary" onClick={() => connect()}>Reconnect</button>
          )}
          {status === 'connected' && (
            <button className="btn btn-sm btn-default" onClick={disconnect}>Disconnect</button>
          )}
        </div>

        <div
          ref={setContainerRef}
          style={{ width: '100%', height: deviceHeight, background: '#1a1a1a', flex: '0 0 auto' }}
        />
      </div>
    </Draggable>
  );
}
